/**
 * Manifest — peer matching + messaging.
 * Native to the /dashboard nav; polling-based inbox (no WebSocket for v1).
 */
import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, MessageSquare, Inbox, Shield, ShieldOff, Send, ArrowLeft, AlertTriangle, Check, X, Flag, Sparkles, UsersRound, BadgeCheck, GraduationCap, MapPin, CalendarDays, Calendar, Lock } from "lucide-react";
import MomentsView from "@/pages/manifest/MomentsView";
import PassiveMatchCard from "@/components/PassiveMatchCard";

const TIER_LABEL = {
  pre_departure_india: "Pre-departure · India",
  same_university:     "Same university",
  same_city:           "Same city, different school",
  same_country:        "Same country",
};

// iter101 · Curated to three low-pressure, genuinely useful first-message
// prompts (visa consulate · arrival timing · course selection). Templated
// with the peer's host_city when available so "flying into Paris" reads
// warmer than "flying in". Kept short so a chip fits on one line at 375px.
const ICEBREAKER_TEMPLATES = [
  { key: "consulate", copy: () => "Which consulate are you booking your visa with?" },
  { key: "arrival",   copy: (city) => `When are you flying into ${city || "your host city"} — few days early or right before class?` },
  { key: "courses",   copy: () => "Have you locked in your course selection yet?" },
];

export default function Manifest({ defaultTab = null }) {
  const [searchParams] = useSearchParams();
  // Allow deep-linking into a specific tab from other pages (e.g. Roam sends
  // ?tab=moments after creating a trip Moment so the user lands in the right
  // place). The value is trusted only for setting the initial tab.
  // iter93 (Item HH) — `defaultTab` prop wins over query string when set, so
  // the standalone /dashboard/moments route can mount this same component
  // with defaultTab="moments" and land the user directly on the Moments tab.
  const validTab = ["matches", "requests", "cohort", "moments", "messages"];
  const paramTab = searchParams.get("tab");
  const initialTab = validTab.includes(defaultTab) ? defaultTab
                   : validTab.includes(paramTab)   ? paramTab
                   : "matches";
  const [tab, setTab] = useState(initialTab);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/manifest/profile").then(r => setProfile(r.data)).catch(() => setProfile({ opt_in_matching: false }));
  }, []);

  if (!profile) return <div className="p-6 text-muted text-sm">Loading Cohort…</div>;

  return (
    <div className="max-w-6xl px-1 sm:px-0" data-testid="manifest-root">
      {/* Back to Dashboard. Persistent across all Manifest tabs.
          iter93 (Cohort P0) — Was rendered inline near the top of the page
          which meant on long match/message lists it scrolled out of reach.
          Now sticky under the fixed Nav (80px tall) so it's ALWAYS one tap
          away regardless of scroll position. Visible pill (bg + border)
          reads as an intentional control rather than a bare text link. */}
      <div className="sticky top-[68px] z-30 -mx-1 sm:mx-0 mb-4 py-2 bg-paper/95 backdrop-blur-sm border-b border-ink/5">
        <Link
          to="/dashboard"
          data-testid="manifest-back-to-dashboard"
          className="inline-flex items-center gap-1.5 pl-2 pr-3.5 py-1.5 rounded-full text-xs font-semibold text-ink/80 hover:text-ink bg-white/70 hover:bg-white border border-ink/12 hover:border-ink/25 transition-colors"
        >
          <ArrowLeft size={13} strokeWidth={2.4} /> Back to dashboard
        </Link>
      </div>

      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-elec font-bold">Cohort</div>
          <h1 className="mt-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Your exchange cohort</h1>
          {/* iter88 Z.2 - removed the "Match with other Indian B-school
              students..." sub-copy line. It duplicated what the tab labels
              already say and cost ~40px of vertical real estate on every
              Manifest visit. */}
        </div>
      </div>

      {/* iter88 Z.1 - cold-state simplification. Until a student opts in,
          the 4 secondary tabs (Requests/Cohort/Moments/Messages) can't do
          anything for them, so we hide the tab bar entirely and show the
          Matches view (which itself renders the opt-in card). Regulars see
          the full tab bar as before. */}
      {profile?.opt_in_matching ? (
        <div className="relative mt-6">
          {/* iter88 BB - Mobile tab-bar fix. Removed icons on mobile
              (text-only is far more compact so all 5 tabs fit at 375px),
              added a fade-gradient edge as an affordance that there's
              more if the bar still overflows on very narrow devices. */}
          <div className="flex gap-1 border-b border-ink/10 overflow-x-auto no-scrollbar" data-testid="manifest-tabs">
            {[
              { k: "matches",  label: "Matches",  icon: Users },
              { k: "requests", label: "Requests", icon: Inbox },
              { k: "cohort",   label: "Cohort",   icon: UsersRound },
              { k: "moments",  label: "Moments",  icon: Calendar },
              { k: "messages", label: "Messages", icon: MessageSquare },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`manifest-tab-${t.k}`}
                className={"px-3 sm:px-4 py-2.5 text-[13px] sm:text-sm font-semibold flex items-center gap-1.5 sm:gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap " +
                  (tab === t.k ? "border-elec text-ink" : "border-transparent text-muted hover:text-ink")}
              >
                <t.icon size={13} className="hidden sm:inline"/> {t.label}
              </button>
            ))}
          </div>
          {/* Right-edge fade so mobile users see there is content beyond
              the visible tabs. Hidden at sm+ (where the strip fits). */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-0 right-0 h-full w-8 sm:hidden"
            style={{ background: "linear-gradient(to left, var(--moe-paper), transparent)" }}
          />
        </div>
      ) : (
        /* iter101 · iter103 — Slim tab bar for opted-out users. Matches
           (their passive discovery view) + Cohort (assembly-ready room,
           passive read only) + Messages (any Say-hi thread they have).
           Every other tab is opt-in gated so we do not render dead buttons. */
        <div className="mt-6 flex gap-1 border-b border-ink/10" data-testid="manifest-tabs-passive">
          {[
            { k: "matches",  label: "Matches",  icon: Users },
            { k: "cohort",   label: "Cohort",   icon: UsersRound },
            { k: "messages", label: "Messages", icon: MessageSquare },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`manifest-tab-${t.k}`}
              className={"px-3 sm:px-4 py-2.5 text-[13px] sm:text-sm font-semibold flex items-center gap-1.5 sm:gap-2 border-b-2 -mb-px transition-colors whitespace-nowrap " +
                (tab === t.k ? "border-elec text-ink" : "border-transparent text-muted hover:text-ink")}
            >
              <t.icon size={13} className="hidden sm:inline"/> {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="mt-6">
        {/* iter101 — Cohort tab reachable even for opted-out users. The
            backend gates read/post separately: assembly-ready cohorts open
            for passive read; posting still requires opt-in.
            iter103 (Priority 1) — Messages tab also reachable so a Say-hi
            wave lands the sender inside the live thread, even before they
            opt in. */}
        {tab === "cohort" && <CohortView profile={profile} />}
        {tab === "messages" && <MessagesView />}
        {tab !== "cohort" && tab !== "messages" && (!profile?.opt_in_matching || tab === "matches") && <MatchesView profile={profile} setProfile={setProfile} />}
        {profile?.opt_in_matching && tab === "requests" && <RequestsView />}
        {profile?.opt_in_matching && tab === "moments"  && <MomentsView profile={profile} />}
      </div>
    </div>
  );
}

/* ============================ MATCHES ============================ */
function MatchesView({ profile, setProfile }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ref, setRef] = useState(null);   // { referral_code, joined_count }
  const [copied, setCopied] = useState(false);
  // email-invite flow
  const [showEmailInvite, setShowEmailInvite] = useState(false);
  const [inviteEmailsRaw, setInviteEmailsRaw] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { sent: [], skipped: [] } | { error }

  useEffect(() => {
    api.get("/manifest/matches").then(r => setData(r.data)).catch(() => setData({ matches: [] }));
    // referral panel — only loaded once
    api.get("/referral/me").then(r => setRef(r.data)).catch(() => setRef(null));
  }, [profile?.opt_in_matching]);

  // Referral link — prefer the backend's absolute URL (built from PUBLIC_APP_URL
  // so shared links ALWAYS point to production, even from a preview env).
  // Fallback to current origin only if the backend didn't provide one.
  const inviteLink = ref?.referral_url
    ? ref.referral_url
    : ref?.referral_code
    ? `${window.location.origin}/signup?ref=${ref.referral_code}`
    : "";
  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked. User can select the text field */ }
  };

  const sendEmailInvite = async () => {
    const emails = inviteEmailsRaw
      .split(/[\s,;]+/)
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
    if (emails.length === 0) {
      setInviteResult({ error: "Add at least one email." });
      return;
    }
    if (emails.length > 10) {
      setInviteResult({ error: "Up to 10 emails per invite." });
      return;
    }
    setInviteSending(true);
    setInviteResult(null);
    try {
      const r = await api.post("/referral/invite", {
        emails,
        note: inviteNote.trim() || undefined,
      });
      setInviteResult(r.data);
      if ((r.data.sent || []).length > 0) {
        setInviteEmailsRaw("");
        setInviteNote("");
      }
    } catch (e) {
      setInviteResult({ error: e?.response?.data?.detail || "Couldn't send. Try again." });
    } finally {
      setInviteSending(false);
    }
  };

  const toggleOptIn = async (val, extra = {}) => {
    setSaving(true);
    try {
      await api.post("/manifest/opt-in", {
        opt_in_matching:       val,
        opt_in_council_search: profile?.opt_in_council_search || false,
        program:               extra.program  ?? profile?.program  ?? null,
        bio:                   extra.bio      ?? profile?.bio      ?? null,
        // iter89 (Item EE) — Optional WhatsApp. Gated on the server:
        // hidden from every match/discovery card until both sides of a
        // connection accept. Copy in the opt-in form is explicit about this.
        whatsapp:              extra.whatsapp ?? profile?.whatsapp ?? null,
      });
      const p = await api.get("/manifest/profile");
      setProfile(p.data);
    } catch (e) {
      // Surface backend validation errors (e.g., program-required) to the caller
      // so the inline opt-in form can render an inline message.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  if (!profile?.opt_in_matching) {
    // iter101 — Passive discovery for opted-out users. The card handles its
    // own data fetch + zero-state. Users see peers first, decide about
    // opting in second — which is the reverse of the earlier gate.
    return (
      <div>
        <PassiveMatchCard />
        <div className="mt-6">
          <ManifestOptInPrompt saving={saving} onOptIn={(program, bio, whatsapp) => toggleOptIn(true, { program, bio, whatsapp })}/>
        </div>
      </div>
    );
  }

  if (!data) return <div className="text-sm text-muted">Loading matches…</div>;

  const grouped = { pre_departure_india: [], same_university: [], same_city: [], same_country: [] };
  (data.matches || []).forEach((m) => grouped[m.match_tier].push(m));

  const total = data.matches?.length || 0;

  return (
    <div>
      {/* iter76 (Item C) — Decluttered header: one horizontal strip on desktop
         (was three stacked full-width panels totalling ~570px above the match
         grid). On mobile it stacks vertically as before, but internal padding
         + typography are tightened so the strip fits under the tab bar
         without dominating the viewport. All prior data + actions preserved:
         count, opt-out, profile editor, referral share. */}
      <div
        className="mt-2 rounded-2xl border border-ink/10 bg-paper overflow-hidden"
        data-testid="matches-summary-strip"
      >
        <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-ink/10">
          {/* Cell 1: count + opt-out. */}
          <div className="p-4 md:p-5" data-testid="matches-summary-panel">
            <div className="flex items-start gap-2">
              <Check size={14} strokeWidth={2.4} className="mt-0.5 text-elec shrink-0"/>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">You're in</div>
                <div className="mt-0.5 font-bold text-ink text-sm leading-snug" data-testid="matches-count-headline">
                  {total === 0
                    ? "No matches yet."
                    : `${total} ${total === 1 ? "match" : "matches"}`}
                </div>
                <button
                  data-testid="manifest-opt-out-btn"
                  onClick={() => toggleOptIn(false)}
                  className="mt-1 text-[11px] font-semibold text-muted hover:text-ink underline"
                >
                  Opt out
                </button>
              </div>
            </div>
          </div>
          {/* Cell 2: inline profile editor (compact). */}
          <div className="p-4 md:p-5">
            <ProfileEditor profile={profile} onSaved={(p) => setProfile(p)} compact />
          </div>
          {/* Cell 3: referral invite, compact. Preserves the copy handler + link
              logic from the original panel but folds the CTA into a single
              inline row so it stops dominating the header. Fuller invite UI
              (multi-email form, note field) still available below the strip
              in the section-scoped invite panel if referral is set. */}
          <div className="p-4 md:p-5">
            {ref?.referral_code ? (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Invite your batch</div>
                <div className="mt-0.5 text-[11px] text-muted leading-snug">
                  Peers you invite land straight in your cohort.
                </div>
                <button
                  type="button"
                  onClick={copyInvite}
                  data-testid="manifest-referral-copy-compact"
                  className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-ink/20 text-[11px] font-semibold text-ink hover:border-ink"
                >
                  {copied ? "Link copied" : "Copy invite link"}
                </button>
              </div>
            ) : (
              <div className="text-[11px] text-muted">Invite classmates from your dashboard.</div>
            )}
          </div>
        </div>
      </div>

      {/* Invite your batch. Always visible once opted in.  Real per-student
          referral loop: unique code from /api/referral/me, honest join count. */}
      {ref?.referral_code && (
        <div className="mt-4 moe-card p-5 sm:p-6" data-testid="referral-invite-panel">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[color:var(--moe-yolk)] text-ink flex items-center justify-center shrink-0">
              <Sparkles size={18} strokeWidth={2.4} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Invite your batch</div>
              <div className="mt-1 font-bold text-ink text-sm sm:text-base">
                Invite classmates headed the same way. They'll land straight in your cohort.
              </div>
              <div className="mt-3 flex items-stretch gap-2 flex-wrap">
                <input
                  data-testid="referral-invite-link"
                  readOnly
                  value={inviteLink}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 min-w-[220px] px-3 py-2 border border-ink/20 rounded-lg text-[13px] text-ink bg-tint focus:outline-none focus:border-elec"
                />
                <button
                  data-testid="referral-copy-btn"
                  onClick={copyInvite}
                  className="moe-btn moe-btn-primary !px-4 shrink-0 text-sm"
                >
                  {copied ? <>Copied ✓</> : <>Copy link</>}
                </button>
              </div>
              <div className="mt-3 text-[12px] text-muted flex items-center gap-1.5" data-testid="referral-joined-count">
                <span className="w-1.5 h-1.5 rounded-full bg-elec" />
                <span className="font-bold text-ink">
                  {ref.joined_count === 0
                    ? "0 so far"
                    : `${ref.joined_count} ${ref.joined_count === 1 ? "person" : "people"} joined via your invite`}
                </span>
                {ref.joined_count === 0 && <span>Be the first to bring someone in.</span>}
              </div>

              {/* Or send it by email. Collapsible so the panel stays calm.
                  POST /api/referral/invite is capped at 10 recipients per call
                  + 30 per user per day server-side; UI hint only. */}
              <div className="mt-3 pt-3 border-t border-ink/10">
                {!showEmailInvite ? (
                  <button
                    data-testid="referral-email-invite-toggle"
                    onClick={() => setShowEmailInvite(true)}
                    className="text-[12px] font-semibold text-elec hover:underline"
                  >
                    Or send it by email →
                  </button>
                ) : (
                  <div data-testid="referral-email-invite-form" className="space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Send by email</div>
                    <textarea
                      data-testid="referral-invite-emails-input"
                      value={inviteEmailsRaw}
                      onChange={(e) => setInviteEmailsRaw(e.target.value)}
                      placeholder="friend@example.com, batch@example.com"
                      rows={2}
                      className="w-full px-3 py-2 border border-ink/20 rounded-lg text-[13px] text-ink bg-white focus:outline-none focus:border-elec"
                    />
                    <textarea
                      data-testid="referral-invite-note-input"
                      value={inviteNote}
                      onChange={(e) => setInviteNote(e.target.value)}
                      placeholder="Optional note (why they should check this out)"
                      rows={2}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-ink/20 rounded-lg text-[13px] text-ink bg-white focus:outline-none focus:border-elec"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        data-testid="referral-invite-send-btn"
                        onClick={sendEmailInvite}
                        disabled={inviteSending || !inviteEmailsRaw.trim()}
                        className="moe-btn moe-btn-primary !px-4 text-sm disabled:opacity-50"
                      >
                        {inviteSending ? "Sending…" : "Send invite"}
                      </button>
                      <button
                        data-testid="referral-invite-cancel-btn"
                        onClick={() => { setShowEmailInvite(false); setInviteResult(null); }}
                        className="text-[12px] font-semibold text-muted hover:text-ink"
                      >
                        Cancel
                      </button>
                      <span className="text-[11px] text-soft">Up to 10 per call · max 30/day</span>
                    </div>
                    {inviteResult?.error && (
                      <div data-testid="referral-invite-error" className="text-[12px] text-red-600">{inviteResult.error}</div>
                    )}
                    {inviteResult && !inviteResult.error && (
                      <div data-testid="referral-invite-result" className="text-[12px] text-ink">
                        {(inviteResult.sent || []).length > 0 && (
                          <div className="text-green-700">
                            Sent to {inviteResult.sent.length} · {inviteResult.sent.join(", ")}
                          </div>
                        )}
                        {(inviteResult.skipped || []).length > 0 && (
                          <div className="text-muted mt-1">
                            Skipped {inviteResult.skipped.length}: {inviteResult.skipped.map(s => `${s.email} (${s.reason})`).join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {total === 0 && (
        <EmptyLine testid="manifest-empty">
          No matches yet — check the Cohort tab for everyone at your host university this semester.
        </EmptyLine>
      )}

      {["pre_departure_india", "same_university", "same_city", "same_country"].map((tier) =>
        grouped[tier].length > 0 && (
          <div key={tier} className="mt-6" data-testid={`manifest-tier-${tier}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{TIER_LABEL[tier]} · {grouped[tier].length}</div>
            <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {grouped[tier].map((m) => <MatchCard key={m.case_id} match={m} />)}
            </div>
          </div>
        )
      )}
    </div>
  );
}

/* ============================ PROFILE EDITOR (iter72) ============================ */

// iter76 (Item C) — Inline opt-in prompt. Replaces the old one-click
// "Opt in to Manifest" button. Now requires a short program label (3-80
// chars) BEFORE opting in — so nobody's first-view card is sparse. Bio stays
// optional (personal storytelling) but if provided must clear a min-30-char
// nudge (enforced server-side too).
function ManifestOptInPrompt({ saving, onOptIn }) {
  const [program,  setProgram]  = useState("");
  const [bio,      setBio]      = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [err,      setErr]      = useState(null);
  // iter89 (Item EE) — bio + program are both required at opt-in now.
  // WhatsApp stays fully optional.
  const canSubmit = program.trim().length >= 3 && bio.trim().length >= 30 && !saving;

  const submit = async () => {
    setErr(null);
    try {
      await onOptIn(program.trim(), bio.trim(), whatsapp.trim() || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't opt in. Try again.");
    }
  };

  return (
    <div className="moe-card p-6 md:p-8" data-testid="manifest-optin-prompt">
      <div className="text-xs uppercase tracking-widest text-elec font-bold">Opt in to see matches</div>
      <h2 className="mt-2 text-2xl font-bold tracking-tight">Only opted-in students appear to each other</h2>
      <p className="mt-2 text-sm text-muted max-w-2xl leading-snug">
        Manifest is off by default. When you opt in, opted-in peers headed to
        the same university, city, or country (with a start date within a few
        weeks of yours) see your first name, host city, and program. You'll see
        theirs. You can hide again anytime.
      </p>

      <div className="mt-6 grid gap-4 max-w-xl">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">
            Program <span className="text-[color:var(--moe-pink)] font-bold ml-0.5">*</span>
          </label>
          <input
            data-testid="manifest-optin-program"
            value={program}
            onChange={(e) => setProgram(e.target.value)}
            maxLength={80}
            placeholder="e.g., MBA · Finance track"
            className="mt-1 w-full px-3 py-2 border border-ink/20 rounded-lg text-sm text-ink bg-white focus:outline-none focus:border-elec"
          />
          <div className="mt-1 flex justify-between text-[10px] text-muted">
            <span>Required. Peers see this on your match card.</span>
            <span className="tabular-nums">{program.length}/80</span>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">
            Short bio <span className="text-[color:var(--moe-pink)] font-bold ml-0.5">*</span>
          </label>
          <textarea
            data-testid="manifest-optin-bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={240}
            rows={3}
            placeholder="A line about you - program, what you're looking to do in Europe. Min 30 characters."
            className="mt-1 w-full px-3 py-2 border border-ink/20 rounded-lg text-sm text-ink bg-white focus:outline-none focus:border-elec resize-none"
          />
          <div className="mt-1 flex justify-between text-[10px] text-muted">
            <span>Required. At least 30 characters.</span>
            <span className="tabular-nums">{bio.length}/240</span>
          </div>
        </div>

        {/* iter89 (Item EE) - Optional WhatsApp. Explicit honesty copy
            about when it becomes visible. No dark pattern, no surprise. */}
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">
            WhatsApp number <span className="text-muted font-normal ml-1">(optional)</span>
          </label>
          <input
            data-testid="manifest-optin-whatsapp"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            maxLength={40}
            placeholder="+91 98xxx xxxxx"
            inputMode="tel"
            autoComplete="tel"
            className="mt-1 w-full px-3 py-2 border border-ink/20 rounded-lg text-sm text-ink bg-white focus:outline-none focus:border-elec"
          />
          <div className="mt-1 flex items-start gap-1.5 text-[11px] text-ink/70 leading-snug" data-testid="cohort-optin-whatsapp-hint">
            <Shield size={11} className="mt-0.5 shrink-0 text-elec" strokeWidth={2.25}/>
            <span>
              Hidden until you both accept a connection. Never shown otherwise.
            </span>
          </div>
        </div>
      </div>

      {err && (
        <div className="mt-4 text-sm text-[color:var(--moe-pink)]" data-testid="manifest-optin-error">
          {err}
        </div>
      )}

      <button
        type="button"
        data-testid="manifest-opt-in-btn"
        onClick={submit}
        disabled={!canSubmit}
        className="mt-6 moe-btn moe-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {saving ? "Opting in…" : "Opt in to Cohort"}
      </button>
    </div>
  );
}

function ProfileEditor({ profile, onSaved, compact = false }) {
  // Collapsible inline editor. In `compact` mode (iter76 strip layout) it
  // renders a single-line label + button; in full mode it keeps the original
  // card layout used elsewhere.
  const [open, setOpen]       = useState(false);
  const [program, setProgram] = useState(profile?.program || "");
  const [bio, setBio]         = useState(profile?.bio || "");
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  useEffect(() => {
    setProgram(profile?.program || "");
    setBio(profile?.bio || "");
  }, [profile?.program, profile?.bio]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.post("/manifest/opt-in", {
        opt_in_matching:       true,   // preserves current opt-in state
        opt_in_council_search: !!profile?.opt_in_council_search,
        display_name:          profile?.display_name || null,
        display_city:          profile?.display_city || null,
        program:               program.trim() || null,
        bio:                   bio.trim() || null,
      });
      const p = await api.get("/manifest/profile");
      onSaved?.(p.data);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const hasContent = !!(profile?.program || profile?.bio);

  // iter76 (Item C) — Compact strip variant. Fits inside the decluttered
  // header strip cell with no borders/padding of its own since the parent
  // grid cell already provides spacing.
  if (compact) {
    return (
      <div data-testid="manifest-profile-editor">
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Your profile</div>
        <div className="mt-0.5 text-[11px] text-muted leading-snug">
          {profile?.program
            ? <>Program set. {profile?.bio ? "Bio set." : "Bio optional."}</>
            : <>Program required. Add now to keep your card informative.</>}
        </div>
        <button
          type="button"
          data-testid="manifest-profile-editor-toggle"
          onClick={() => setOpen(o => !o)}
          className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-ink/20 text-[11px] font-semibold text-ink hover:border-ink"
        >
          {open ? "Close" : (hasContent ? "Edit profile" : "Complete profile")}
        </button>
        {open && (
          <div className="mt-3 space-y-2">
            <input
              data-testid="manifest-profile-program"
              value={program}
              onChange={(e) => setProgram(e.target.value)}
              maxLength={80}
              placeholder="Program (required)"
              className="w-full px-2.5 py-1.5 border border-ink/20 rounded-lg text-[12px] text-ink bg-white focus:outline-none focus:border-elec"
            />
            <textarea
              data-testid="manifest-profile-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={240}
              rows={2}
              placeholder="Short bio (optional, min 30 chars if provided)"
              className="w-full px-2.5 py-1.5 border border-ink/20 rounded-lg text-[12px] text-ink bg-white focus:outline-none focus:border-elec resize-none"
            />
            <button
              type="button"
              data-testid="manifest-profile-save"
              onClick={save}
              disabled={saving || program.trim().length < 3}
              className="moe-btn moe-btn-primary !py-1.5 !px-3 text-[11px] disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {saved && <span className="ml-2 text-[11px] text-elec font-semibold" data-testid="manifest-profile-saved">Saved.</span>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 moe-card p-5 sm:p-6" data-testid="manifest-profile-editor">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Your Cohort profile</div>
          <div className="mt-1 text-sm font-bold text-ink leading-snug">
            {hasContent
              ? "Program and bio shown on your match card."
              : "Add a short program + bio so peers can decide whether to connect."}
          </div>
          <div className="mt-0.5 text-xs text-muted leading-snug">
            Both fields are optional. Empty means peers see "Not shared". Nothing is invented.
          </div>
        </div>
        <button
          type="button"
          data-testid="manifest-profile-editor-toggle"
          onClick={() => setOpen(o => !o)}
          className="text-xs font-semibold text-elec hover:underline shrink-0"
        >
          {open ? "Close" : hasContent ? "Edit" : "Add"}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Program</label>
            <input
              data-testid="manifest-profile-program"
              value={program}
              onChange={(e) => setProgram(e.target.value)}
              maxLength={80}
              placeholder="e.g., MBA · Finance track"
              className="mt-1 w-full px-3 py-2 border border-ink/20 rounded-lg text-[13px] text-ink bg-white focus:outline-none focus:border-elec"
            />
            <div className="mt-1 text-[10px] text-muted tabular-nums">{program.length}/80</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Short bio</label>
            <textarea
              data-testid="manifest-profile-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={240}
              rows={3}
              placeholder="A couple of lines about you. Background, what you're hoping to do on exchange."
              className="mt-1 w-full px-3 py-2 border border-ink/20 rounded-lg text-[13px] text-ink bg-white focus:outline-none focus:border-elec resize-none"
            />
            <div className="mt-1 text-[10px] text-muted tabular-nums">{bio.length}/240</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="manifest-profile-save"
              onClick={save}
              disabled={saving}
              className="moe-btn moe-btn-primary !py-2 !px-4 text-xs disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {saved && <div className="text-xs text-elec font-semibold" data-testid="manifest-profile-saved">Saved.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function MatchCard({ match }) {
  const [state, setState] = useState("idle"); // idle | sent | error
  const [error, setError] = useState("");

  const connect = async () => {
    try {
      await api.post("/manifest/connect", { target_case_id: match.case_id, match_tier: match.match_tier });
      setState("sent");
    } catch (e) {
      setState("error");
      setError(e.response?.data?.detail || "Could not send request");
    }
  };

  // iter72 (Item 4) — richer decision-making fields. Honest UX rule: if a
  // field wasn't shared, render "Not shared" instead of hiding the card or
  // fabricating a placeholder. Every visible datum here is real DB data.
  const startLabel = match.start_date ? _fmtIso(match.start_date) : null;
  const durLabel   = match.duration_days ? `${match.duration_days}d` : null;

  return (
    <div className="moe-card p-4 flex flex-col" data-testid={`match-${match.case_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-elec/10 text-elec font-bold flex items-center justify-center shrink-0 text-sm">
          {match.display_name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="font-bold text-ink text-sm truncate">{match.display_name}</div>
            {/* iter76 (Item C) — Verified check kept inline as tiny icon;
                previous badge pill removed to reduce visual noise. Tier label
                moved from per-card row up to the section header (redundant
                otherwise). Referred badge stays inline since it varies per
                card. */}
            {match.verified && (
              <BadgeCheck
                data-testid={`verified-badge-${match.case_id}`}
                size={12}
                strokeWidth={2.5}
                className="text-elec shrink-0"
              />
            )}
            {match.referred && (
              <span
                data-testid={`referred-badge-${match.case_id}`}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold text-ink bg-[color:var(--moe-yolk)]"
                title="Joined via your invite"
              >
                Your invite
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted truncate">
            {match.host_university || match.display_city}
          </div>
          {/* iter91 · honest "last active" signal — real timestamp, hides
              entirely when null or >24h old. No fake online status. */}
          {(() => {
            const activeLabel = _fmtLastActive(match.last_active_at);
            if (!activeLabel) return null;
            return (
              <div
                data-testid={`match-last-active-${match.case_id}`}
                className="mt-0.5 text-[10.5px] text-mint-ink"
                style={{ color: "var(--moe-elec)" }}
              >
                {activeLabel}
              </div>
            );
          })()}
          {match.jurisdiction_label && (
            <div
              data-testid={`match-jurisdiction-label-${match.case_id}`}
              className="mt-1 text-[11px] text-ink/80 leading-snug"
            >
              {match.jurisdiction_label}
            </div>
          )}
        </div>
      </div>

      {/* iter95 (Cohort P0) — added "Destination university" as an explicit
         labeled row. For same-country / same-city match tiers, this is the
         single most-decisive datum: two students in the same country may
         actually be at completely different host institutions, and the peer
         couldn't tell before. Data already flowed on the payload (host_university
         is projected in _public_profile_for_case). DetailRow renders
         "Not shared" for null so honest empty state is preserved. Grid widens
         to 3 cols on md+ so 5 fields land in a clean 3+2 layout. */}
      <dl
        data-testid={`match-details-${match.case_id}`}
        className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1.5 text-[11px]"
      >
        <DetailRow label="Destination" value={match.host_city ? `${match.host_city}, ${match.country}` : (match.country || null)} />
        <DetailRow label="Destination university" value={match.host_university || null} testid={`match-host-university-${match.case_id}`} />
        <DetailRow label="Dates" value={startLabel && durLabel ? `${startLabel} · ${durLabel}` : startLabel || null} />
        <DetailRow label="Home university" value={match.home_university || null} />
        <DetailRow label="Program" value={match.program || null} testid={`match-program-${match.case_id}`} />
      </dl>
      {match.bio ? (
        <div
          data-testid={`match-bio-${match.case_id}`}
          className="mt-2 text-[12px] text-ink/80 leading-snug border-l-2 border-elec/40 pl-2.5 line-clamp-2"
        >
          {match.bio}
        </div>
      ) : (
        <div data-testid={`match-bio-empty-${match.case_id}`} className="hidden" />
      )}

      <div className="mt-3 pt-3 border-t border-ink/10">
        {state === "idle" && (
          <button data-testid={`connect-${match.case_id}`} onClick={connect} className="w-full moe-btn moe-btn-primary !py-2 text-xs">
            Connect
          </button>
        )}
        {state === "sent" && <div className="text-center text-xs text-muted font-semibold">Request sent · awaiting response</div>}
        {state === "error" && <div className="text-center text-xs text-[color:var(--moe-pink)] font-semibold">{error}</div>}
      </div>
    </div>
  );
}

// iter72 helper — kv row used by MatchCard + RequestCard. `value` is either
// the real string (renders normally) or falsy (renders "Not shared" in muted).
function DetailRow({ label, value, testid }) {
  const isSet = !!value;
  return (
    <div className="min-w-0" data-testid={testid}>
      <dt className="text-[9px] uppercase tracking-widest text-muted font-bold">{label}</dt>
      <dd className={`text-[11px] truncate ${isSet ? "text-ink" : "text-ink/40 italic"}`}>
        {isSet ? value : "Not shared"}
      </dd>
    </div>
  );
}

// Small YYYY-MM-DD → "1 Sep 2026" formatter shared across cards.
function _fmtIso(iso) {
  try {
    const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${d} ${months[m - 1]} ${y}`;
  } catch { return iso; }
}

// iter91 · Honest "last active" formatter. Returns null when we have no
// stamp OR when the stamp is older than 24h (the frontend hides the line
// entirely in that case — no misleading "long time ago" copy, no fake
// "online now" green dot). Real timestamps only.
function _fmtLastActive(iso) {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (!ts) return null;
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return null;
  const mins  = Math.floor(diffMs / 60000);
  const hours = Math.floor(mins / 60);
  if (hours >= 24) return null;              // hide when >24h
  if (mins  < 2)   return "active just now";
  if (mins  < 60)  return `active ${mins} min ago`;
  return `active ${hours}h ago`;
}

// iter91 (Z.3) · Shared compact empty-state — a single muted line that never
// competes visually with populated sections. All 5 Manifest sub-view empty
// states route through this primitive so they read as one calm, consistent
// beat instead of five different full-weight cards.
function EmptyLine({ testid, children }) {
  return (
    <div data-testid={testid} className="py-6 text-center text-[12px] text-muted italic">
      {children}
    </div>
  );
}

/* ============================ REQUESTS ============================ */
function RequestsView() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/manifest/requests").then(r => setRows(r.data.requests || []));
  useEffect(() => { load(); }, []);

  const respond = async (connection_id, action) => {
    await api.post("/manifest/respond", { connection_id, action });
    load();
  };

  if (!rows) return <div className="text-sm text-muted">Loading requests…</div>;
  if (rows.length === 0) return (
    <EmptyLine testid="requests-empty">No incoming requests right now.</EmptyLine>
  );

  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="requests-list">
      {rows.map((r) => {
        const f = r.from || {};
        const startLabel = f.start_date ? _fmtIso(f.start_date) : null;
        const durLabel   = f.duration_days ? `${f.duration_days}d` : null;
        return (
          <div key={r.id} className="moe-card p-4" data-testid={`request-${r.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-elec/10 text-elec font-bold flex items-center justify-center shrink-0">
                {f.display_name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-ink text-sm truncate">{f.display_name || "Student"}</div>
                <div className="text-[11px] text-muted truncate">{f.host_university || f.display_city}</div>
                <div className="text-[10px] uppercase tracking-widest text-elec font-bold mt-1">{TIER_LABEL[r.match_tier]}</div>
              </div>
            </div>

            {/* iter72 (Item 4). Richer request card. Same key/value strip and
               bio treatment as MatchCard so the recipient can make an informed
               accept/decline decision on the same evidence the requester saw.
               iter95 (Cohort P0) — added "Destination university" row so a
               same-country/same-city request is legible (which host institution
               they're actually at). DetailRow handles null → "Not shared". */}
            <dl
              data-testid={`request-details-${r.id}`}
              className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1.5 text-[11px]"
            >
              <DetailRow label="Destination" value={f.host_city ? `${f.host_city}, ${f.country}` : (f.country || null)} />
              <DetailRow label="Destination university" value={f.host_university || null} testid={`request-host-university-${r.id}`} />
              <DetailRow label="Dates" value={startLabel && durLabel ? `${startLabel} · ${durLabel}` : startLabel || null} />
              <DetailRow label="Home university" value={f.home_university || null} />
              <DetailRow label="Program" value={f.program || null} testid={`request-program-${r.id}`} />
            </dl>
            {f.bio ? (
              <div
                data-testid={`request-bio-${r.id}`}
                className="mt-3 text-[12px] text-ink/80 leading-snug border-l-2 border-elec/40 pl-3"
              >
                {f.bio}
              </div>
            ) : (
              <div
                data-testid={`request-bio-empty-${r.id}`}
                className="mt-3 text-[11px] text-ink/50 italic"
              >
                Bio not shared.
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-ink/10 flex gap-2">
              <button data-testid={`accept-${r.id}`} onClick={() => respond(r.id, "accept")}
                className="flex-1 moe-btn moe-btn-primary !py-2 text-xs"><Check size={12} /> Accept</button>
              <button data-testid={`decline-${r.id}`} onClick={() => respond(r.id, "decline")}
                className="flex-1 moe-btn !py-2 text-xs border border-ink/25"><X size={12} /> Decline</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================ COHORT (group thread — host_university + semester) ============================ */
function CohortView({ profile }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = () => api.get("/manifest/cohort").then((r) => setData(r.data));
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post("/manifest/cohort/send", { text: text.trim() });
      setText("");
      load();
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="text-sm text-muted">Loading cohort…</div>;
  if (data.needs_opt_in) {
    return (
      <div className="moe-card p-8" data-testid="cohort-optin-prompt">
        <div className="text-xs uppercase tracking-widest text-elec font-bold">Cohort. Group thread for your semester</div>
        <h2 className="mt-2 text-2xl font-bold tracking-tight">Waiting for your cohort to form</h2>
        <p className="mt-2 text-sm text-muted max-w-2xl">
          A group chat auto-forms the moment three students confirm the same host university and semester. Opt in from the Matches tab and you'll be the first name in the room.
        </p>
      </div>
    );
  }

  const passiveAccess = !!data.passive_access;   // iter101 — assembly-ready cohort, caller not yet opted in
  const canPost       = data.can_post !== false; // undefined for legacy responses → treat as opted in

  const [uniLabel, semester] = [data.host_university || "Your university", data.semester || ""];
  const members = data.members || [];
  const cityLabel = (() => {
    // Most students in a cohort share the host city; use the modal one as label if unanimous
    const cities = members.map(m => m.display_city).filter(Boolean);
    if (!cities.length) return "";
    const counts = {}; cities.forEach(c => { counts[c] = (counts[c] || 0) + 1; });
    return Object.entries(counts).sort((a,b)=>b[1]-a[1])[0][0];
  })();

  return (
    <div className="space-y-5" data-testid="cohort-view">
      {/* iter101 — passive read banner. Only renders when the caller isn't
          opted in but the cohort has crossed the assembly threshold. */}
      {passiveAccess && (
        <div
          className="rounded-2xl border border-teal-500/25 bg-teal-500/[0.06] px-4 py-3 md:px-5 md:py-3.5 flex items-start gap-3"
          data-testid="cohort-passive-banner"
        >
          <div className="w-9 h-9 rounded-full bg-teal-500/15 text-teal-700 flex items-center justify-center shrink-0">
            <UsersRound size={16} strokeWidth={2.2}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-bold text-ink leading-snug">
              You're in the room, quietly.
            </div>
            <div className="mt-0.5 text-[12px] text-ink/70 leading-snug">
              Read every message. Opt in from Matches when you want to reply — takes about a minute.
            </div>
          </div>
        </div>
      )}
      {/* ============ COHORT HEADER. The "who is in your cohort" identity ============ */}
      <div className="moe-card p-5 sm:p-6" data-testid="cohort-header">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-elec text-white flex items-center justify-center shrink-0">
            <UsersRound size={18} strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Cohort</div>
            <div className="mt-1 font-bold text-ink text-base sm:text-lg" data-testid="cohort-title">
              {uniLabel} {semester && <span className="text-muted font-medium">· {semester}</span>}
            </div>
            <div className="mt-0.5 text-xs sm:text-[13px] text-muted" data-testid="cohort-member-count">
              {members.length} {members.length === 1 ? "student" : "students"} confirmed{cityLabel && <> · {cityLabel}</>}
            </div>
          </div>
        </div>
      </div>

      {/* ============ MEMBER ROSTER. Clear cards per matched student ============ */}
      {members.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold mb-2">
            Who's in your cohort
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="cohort-members">
            {members.map((m) => {
              const mine = m.case_id === data.me_case_id;
              return (
                <div
                  key={m.case_id}
                  data-testid={`cohort-member-${m.case_id}`}
                  className={`p-3.5 rounded-xl border ${mine ? "border-elec bg-elec/5" : "border-ink/12 bg-white"} flex items-start gap-3`}
                >
                  <div className={`w-10 h-10 rounded-full font-bold flex items-center justify-center shrink-0 text-sm ${mine ? "bg-elec text-white" : "bg-elec/10 text-elec"}`}>
                    {m.display_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="font-bold text-ink text-sm truncate">
                        {m.display_name}{mine && <span className="ml-1 text-[9px] uppercase tracking-widest text-elec font-bold">· you</span>}
                      </div>
                      {m.verified && (
                        <span
                          title="Verified student. Real case, confirmed university and dates"
                          className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest font-bold text-elec bg-elec/10 px-1.5 py-0.5 rounded-full shrink-0"
                        >
                          <BadgeCheck size={10} strokeWidth={2.5} />
                        </span>
                      )}
                    </div>
                    {m.home_university && (
                      <div className="mt-0.5 text-[11.5px] text-muted truncate flex items-center gap-1">
                        <GraduationCap size={11} strokeWidth={2.2} />
                        {m.home_university}
                      </div>
                    )}
                    {m.display_city && (
                      <div className="mt-0.5 text-[11px] text-muted truncate flex items-center gap-1">
                        <MapPin size={10} strokeWidth={2.2} />
                        {m.display_city}
                      </div>
                    )}
                    {/* iter91 · honest "last active" — real timestamp, hidden if null or >24h. */}
                    {(() => {
                      const activeLabel = _fmtLastActive(m.last_active_at);
                      if (!activeLabel) return null;
                      return (
                        <div
                          data-testid={`cohort-last-active-${m.case_id}`}
                          className="mt-0.5 text-[10.5px]"
                          style={{ color: "var(--moe-elec)" }}
                        >
                          {activeLabel}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ GROUP THREAD ============ */}
      <div className="moe-card overflow-hidden" data-testid="cohort-thread">
        <div className="p-3.5 border-b border-ink/10 flex items-center gap-2.5">
          <MessageSquare size={15} className="text-elec shrink-0" strokeWidth={2.2} />
          <div className="font-bold text-ink text-sm">Group thread</div>
          <div className="text-[11px] text-muted">· everyone above sees your messages</div>
        </div>
        <div ref={scrollRef} className="h-[55vh] max-h-[500px] min-h-[280px] overflow-y-auto p-3.5 space-y-2.5" data-testid="cohort-messages">
          {(data.messages || []).map((m) => {
            const mine = m.sender_case_id === data.me_case_id;
            return (
              <div key={m.id} className={"flex " + (mine ? "justify-end" : "justify-start")} data-testid={`cohort-msg-${m.id}`}>
                <div className={"max-w-[80%] " + (mine ? "text-right" : "text-left")}>
                  <div className="text-[10px] uppercase tracking-widest text-soft font-bold mb-1">
                    {mine ? "You" : m.sender_display_name}
                  </div>
                  <div className={"px-3 py-2 rounded-2xl text-sm inline-block " + (mine ? "bg-ink text-white" : "bg-tint text-ink")}>
                    {m.text}
                  </div>
                </div>
              </div>
            );
          })}
          {(data.messages || []).length === 0 && (
            <EmptyLine testid="cohort-empty">
              No messages yet — be the first to say hi to your {uniLabel} cohort.
            </EmptyLine>
          )}
        </div>

        <form onSubmit={send} className="p-3 border-t border-ink/10">
          <div className="flex gap-2">
            <input
              data-testid="cohort-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={canPost ? `Message everyone in this cohort…` : `Opt in from Matches to join the conversation`}
              className="flex-1 min-w-0 px-3 py-2 border border-ink/20 rounded-lg text-sm focus:outline-none focus:border-elec disabled:bg-ink/5 disabled:text-ink/50"
              maxLength={2000}
              disabled={!canPost}
            />
            <button data-testid="cohort-send" type="submit" disabled={sending || !canPost} className="moe-btn moe-btn-primary !px-4 shrink-0 disabled:opacity-50">
              <Send size={14} />
            </button>
          </div>
          {/* iter99 (BBB.3) — quiet encryption indicator on cohort composer. */}
          <div className="mt-1.5 pl-1 flex items-center gap-1 text-[10.5px] text-muted" data-testid="cohort-encrypted-indicator">
            <Lock size={10} className="text-elec/80" strokeWidth={2.25}/>
            <span>Encrypted ·</span>
            <Link to="/security" target="_blank" rel="noopener" className="text-elec/90 underline hover:text-elec" data-testid="cohort-security-link">
              Learn more
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================ MESSAGES ============================ */
function MessagesView() {
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null); // { id, other }
  const load = () => api.get("/manifest/conversations").then(r => setConvs(r.data.conversations || []));

  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);

  // iter103 (Priority 1) — deep-link support. When a URL carries
  // ?tab=messages&convo=<id>, auto-open that conversation once the list
  // has loaded. Used by the passive-match Say-hi flow.
  useEffect(() => {
    if (!convs || convs.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("convo");
    if (!wanted) return;
    const match = convs.find((c) => c.id === wanted);
    if (match) setActive(match);
  }, [convs]);

  if (!convs) return <div className="text-sm text-muted">Loading messages…</div>;
  if (convs.length === 0) return (
    <EmptyLine testid="messages-empty">
      No conversations yet. Wave to a peer from your Matches list to start one.
    </EmptyLine>
  );

  if (active) {
    return <Conversation conversation={active} onBack={() => { setActive(null); load(); }} />;
  }

  return (
    <div className="moe-card divide-y divide-ink/10" data-testid="conversations-list">
      {convs.map((c) => (
        <button key={c.id} onClick={() => setActive(c)}
          data-testid={`open-conv-${c.id}`}
          className="w-full p-4 flex items-start gap-3 hover:bg-tint text-left">
          <div className="w-10 h-10 rounded-full bg-elec/10 text-elec font-bold flex items-center justify-center shrink-0">
            {c.other?.display_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <div className="font-bold text-ink text-sm truncate">{c.other?.display_name || "Student"}</div>
              {c.unread > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-elec text-white font-bold">{c.unread}</span>}
            </div>
            <div className="text-[11px] text-muted truncate">
              {c.last_message?.text || "New conversation"}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Conversation({ conversation, onBack }) {
  const [msgs, setMsgs] = useState([]);
  const [meCaseId, setMeCaseId] = useState(null);
  const [text, setText] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const scrollRef = useRef(null);
  const lastTypingSentRef = useRef(0);   // ms timestamp of last POST to /typing
  const stopTypingTimerRef = useRef(null);
  const conv = conversation;

  const load = () => api.get(`/manifest/messages?conversation_id=${conv.id}`).then(r => {
    setMsgs(r.data.messages || []);
    if (r.data.me_case_id) setMeCaseId(r.data.me_case_id);
    setOtherTyping(!!r.data.other_typing);
  });
  // iter90 (Item CC) — poll faster (3s) than the cohort thread (6s) so the
  // typing indicator feels close to real-time. Still just polling, no sockets.
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [conv.id]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs.length, otherTyping]);

  // iter90 (Item CC) — clean up on unmount: fire an explicit typing:false so
  // the other side doesn't briefly see a stale pill after we navigate away.
  useEffect(() => () => {
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    api.post("/manifest/typing", { conversation_id: conv.id, typing: false }).catch(() => {});
  }, [conv.id]);

  // Throttled "I'm typing" ping: fires at most once every 3s while the user
  // is actively editing. A 4s idle timer follows up with typing:false so the
  // indicator vanishes even without an explicit blur/send.
  const pingTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current > 3000) {
      lastTypingSentRef.current = now;
      api.post("/manifest/typing", { conversation_id: conv.id, typing: true }).catch(() => {});
    }
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    stopTypingTimerRef.current = setTimeout(() => {
      api.post("/manifest/typing", { conversation_id: conv.id, typing: false }).catch(() => {});
    }, 4000);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    lastTypingSentRef.current = 0;
    await api.post("/manifest/send", { conversation_id: conv.id, text: text.trim() });
    setText("");
    load();
  };

  const block = async () => {
    if (!window.confirm("Block this person? You won't be able to message them and they won't be able to message you.")) return;
    await api.post("/manifest/respond", { connection_id: conv.connection_id, action: "block" });
    onBack();
  };

  // iter90 (Item DD) — Group consecutive messages from the same sender so we
  // only render one bubble stack per burst (Apple/Signal-style). A gap of
  // >5 minutes breaks the group and re-shows the time header.
  const groupedMsgs = useMemo(() => {
    const out = [];
    let lastSender = null;
    let lastTs = 0;
    for (const m of msgs) {
      const ts = Date.parse(m.sent_at || "") || 0;
      const breakGroup = m.sender_case_id !== lastSender || (ts - lastTs > 5 * 60 * 1000);
      out.push({ ...m, groupStart: breakGroup, ts });
      lastSender = m.sender_case_id;
      lastTs = ts;
    }
    return out;
  }, [msgs]);

  const otherName = conv.other?.display_name || "Student";
  const otherCity = conv.other?.display_city || "";
  const otherInitial = otherName[0]?.toUpperCase() || "?";

  return (
    <div className="moe-card overflow-hidden flex flex-col" data-testid="conversation-view">
      {/* Header — cleaner spacing + honest subtitle (city, no fake online status) */}
      <div className="px-4 py-3 border-b border-ink/10 flex items-center gap-3 bg-white">
        <button onClick={onBack} data-testid="conv-back" aria-label="Back to conversations"
                className="p-1.5 hover:bg-tint rounded-lg transition-colors">
          <ArrowLeft size={16} />
        </button>
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-elec/20 to-mint/30 text-elec font-bold flex items-center justify-center text-sm shrink-0">
          {otherInitial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-ink text-sm truncate leading-tight" data-testid="conv-other-name">{otherName}</div>
          <div className="text-[11px] text-muted truncate">{otherCity || "\u00A0"}</div>
        </div>
        <button onClick={() => setShowReport(true)} data-testid="conv-report" title="Report" className="p-1.5 hover:bg-tint rounded-lg transition-colors">
          <Flag size={14} className="text-muted" />
        </button>
        <button onClick={block} data-testid="conv-block" title="Block" className="p-1.5 hover:bg-tint rounded-lg transition-colors">
          <ShieldOff size={14} className="text-muted" />
        </button>
      </div>

      {/* Scrollable message area on a subtle tint so the paper card lifts the bubbles */}
      <div ref={scrollRef} className="h-[55vh] max-h-[520px] min-h-[300px] overflow-y-auto p-4 space-y-1 bg-tint/40" data-testid="conv-messages">
        {groupedMsgs.map((m) => {
          const mine = meCaseId && m.sender_case_id === meCaseId;
          const seenByOther = mine && Array.isArray(m.read_by)
            && m.read_by.some((cid) => cid && cid !== meCaseId);
          const timeLabel = m.ts
            ? new Date(m.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            : "";
          return (
            <div key={m.id} data-testid={`msg-${m.id}`}>
              {m.groupStart && (
                <div className={"flex items-center gap-2 pt-3 pb-1 " + (mine ? "justify-end" : "justify-start")}>
                  {!mine && (
                    <div className="text-[11px] font-semibold text-ink/80 leading-none">{otherName}</div>
                  )}
                  <div className="text-[10px] text-muted uppercase tracking-wide leading-none">{timeLabel}</div>
                </div>
              )}
              <div className={"flex " + (mine ? "justify-end" : "justify-start")}>
                <div
                  className={
                    "max-w-[78%] px-3.5 py-2 text-sm leading-snug break-words " +
                    (mine
                      ? "bg-ink text-white rounded-2xl rounded-br-md shadow-sm"
                      : "bg-white text-ink border border-ink/10 rounded-2xl rounded-bl-md shadow-sm")
                  }
                >
                  {m.text}
                </div>
              </div>
              {mine && seenByOther && (
                <div className="flex justify-end pr-1 pt-0.5">
                  <div className="text-[10px] text-muted flex items-center gap-1" data-testid={`msg-seen-${m.id}`}>
                    <Check size={10} strokeWidth={3} className="-mr-1.5" />
                    <Check size={10} strokeWidth={3} />
                    Seen
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Typing indicator — three animated dots inside a bubble on the OTHER side */}
        {otherTyping && (
          <div className="flex justify-start pt-2" data-testid="conv-typing">
            <div className="bg-white border border-ink/10 rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-bounce" style={{ animationDelay: "0ms", animationDuration: "1s" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-bounce" style={{ animationDelay: "150ms", animationDuration: "1s" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-ink/40 animate-bounce" style={{ animationDelay: "300ms", animationDuration: "1s" }} />
            </div>
          </div>
        )}

        {msgs.length === 0 && !otherTyping && (
          <div data-testid="conv-icebreaker" className="pt-6 pb-2">
            <div className="text-center text-xs text-muted">
              <Sparkles size={14} className="inline mr-1 -mt-0.5" /> First message. Pick a prompt or write your own
            </div>
            <div className="mt-4 flex flex-col gap-2 max-w-md mx-auto">
              {ICEBREAKER_TEMPLATES.map((t, i) => {
                const prompt = t.copy(otherCity);
                return (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`icebreaker-${t.key}`}
                    onClick={() => setText(prompt)}
                    className="text-left px-3 py-2 text-xs rounded-xl border border-ink/12 hover:border-elec hover:bg-elec/5 transition-colors leading-snug bg-white"
                  >
                    {prompt}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modernised composer: pill input, integrated send, honest 2000-char limit hint on overflow */}
      <form onSubmit={send} className="p-3 border-t border-ink/10 bg-white">
        <div className="flex items-center gap-2 bg-tint/60 rounded-full px-4 py-1.5 border border-ink/10 focus-within:border-elec transition-colors">
          <input
            data-testid="conv-input"
            value={text}
            onChange={(e) => { setText(e.target.value); if (e.target.value.trim()) pingTyping(); }}
            placeholder="Message…"
            className="flex-1 min-w-0 bg-transparent py-2 text-sm placeholder:text-muted focus:outline-none"
            maxLength={2000}
          />
          <button
            data-testid="conv-send"
            type="submit"
            disabled={!text.trim()}
            aria-label="Send message"
            className={
              "shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all " +
              (text.trim()
                ? "bg-ink text-white hover:scale-105 active:scale-95"
                : "bg-ink/10 text-ink/30 cursor-not-allowed")
            }
          >
            <Send size={14} />
          </button>
        </div>
        {/* iter99 (BBB.3) — quiet encryption-at-rest indicator. Deliberately
            small; not competing with the send button. Links to /security for
            full detail. */}
        <div className="mt-1.5 pl-1 flex items-center gap-1 text-[10.5px] text-muted" data-testid="conv-encrypted-indicator">
          <Lock size={10} className="text-elec/80" strokeWidth={2.25}/>
          <span>Encrypted ·</span>
          <Link to="/security" target="_blank" rel="noopener" className="text-elec/90 underline hover:text-elec" data-testid="conv-security-link">
            Learn more
          </Link>
        </div>
      </form>

      {showReport && (
        <ReportDialog
          conversation_id={conv.id}
          reported_case_id={conv.other?.case_id || ""}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  );
}

function ReportDialog({ conversation_id, reported_case_id, onClose }) {
  const [reason, setReason] = useState("harassment");
  const [details, setDetails] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async () => {
    await api.post("/manifest/report", { conversation_id, reported_case_id, reason, details });
    setSent(true);
    setTimeout(onClose, 1600);
  };

  return (
    <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4" data-testid="report-dialog">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full">
        <div className="text-xs uppercase tracking-widest text-elec font-bold">Report</div>
        {!sent ? (
          <>
            <h3 className="mt-2 text-lg font-bold">What happened?</h3>
            <div className="mt-4 space-y-2">
              {["harassment","inappropriate","spam","other"].map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="reason" checked={reason===r} onChange={() => setReason(r)} data-testid={`report-reason-${r}`} />
                  <span className="capitalize">{r}</span>
                </label>
              ))}
            </div>
            <textarea
              data-testid="report-details"
              value={details} onChange={(e) => setDetails(e.target.value)}
              placeholder="Optional details for the moderators"
              rows={3}
              maxLength={1000}
              className="mt-3 w-full px-3 py-2 border border-ink/20 rounded-lg text-sm focus:outline-none focus:border-elec"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={onClose} className="moe-btn !py-2 text-xs border border-ink/25">Cancel</button>
              <button onClick={submit} data-testid="report-submit" className="moe-btn moe-btn-primary !py-2 text-xs">Submit report</button>
            </div>
          </>
        ) : (
          <div className="text-sm text-ink mt-3">Thanks. The moderators will review this shortly.</div>
        )}
      </div>
    </div>
  );
}
